import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';

const MONTSERRAT = { fontFamily: "'Montserrat', sans-serif" };

// Hardcoded KOVA branding - landing page never uses agency theming
const KOVA = {
  brandName: 'KOVA',
  logoUrl: null as string | null,
  primary: '#412F21',
  primaryRgb: '65, 47, 33',
  background: '#FBF9F6',
  backgroundRgb: '251, 249, 246',
  headingText: '#1A1A1A',
  secondaryText: '#6B6B6B',
};

export function Landing_1() {
  const navigate = useNavigate();
  const [scrolledPastHero, setScrolledPastHero] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const heroHeight = window.innerHeight;
      setScrolledPastHero(window.scrollY > heroHeight - 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogin = () => {
    navigate('/');
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div
      className="min-h-screen overflow-x-hidden"
      style={{
        backgroundColor: '#FBF9F6',
        ...MONTSERRAT,
      }}
    >
      {/* Header */}
      <header
        className="fixed top-0 left-0 right-0 z-50 px-8 transition-all duration-300"
        style={{
          backgroundColor: scrolledPastHero ? '#FBF9F6' : 'transparent',
          boxShadow: scrolledPastHero ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
          paddingTop: scrolledPastHero ? '1rem' : '1.5rem',
          paddingBottom: scrolledPastHero ? '1rem' : '1.5rem',
        }}
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={scrollToTop}
            className="flex items-center hover:opacity-70 transition-opacity"
          >
            {KOVA.logoUrl ? (
              <img
                src={KOVA.logoUrl}
                alt={KOVA.brandName}
                className="h-10 object-contain"
                style={{ maxWidth: '160px' }}
              />
            ) : (
              <span
                className="uppercase font-medium transition-colors duration-300"
                style={{
                  color: scrolledPastHero ? '#412F21' : '#fff',
                  fontSize: '14px',
                  letterSpacing: '0.5em',
                  textShadow: scrolledPastHero ? 'none' : '0 1px 2px rgba(0,0,0,0.1)'
                }}
              >
                {KOVA.brandName}
              </span>
            )}
          </button>

          {/* Center nav */}
          <nav className="hidden md:flex items-center gap-10">
            <button
              onClick={() => scrollToSection('features')}
              className="text-xs tracking-widest uppercase hover:opacity-70 transition-all duration-300"
              style={{
                color: scrolledPastHero ? '#412F21' : '#fff',
                textShadow: scrolledPastHero ? 'none' : '0 1px 2px rgba(0,0,0,0.1)',
                ...MONTSERRAT
              }}
            >
              Features
            </button>
            <button
              onClick={() => scrollToSection('pricing')}
              className="text-xs tracking-widest uppercase hover:opacity-70 transition-all duration-300"
              style={{
                color: scrolledPastHero ? '#412F21' : '#fff',
                textShadow: scrolledPastHero ? 'none' : '0 1px 2px rgba(0,0,0,0.1)',
                ...MONTSERRAT
              }}
            >
              Price
            </button>
          </nav>

          {/* Login - Hidden for now */}
          {false && <button
            onClick={handleLogin}
            className="text-xs tracking-widest uppercase hover:opacity-70 transition-all duration-300"
            style={{
              color: scrolledPastHero ? '#412F21' : '#fff',
              textShadow: scrolledPastHero ? 'none' : '0 1px 2px rgba(0,0,0,0.1)',
              ...MONTSERRAT
            }}
          >
            Login
          </button>}
        </div>
      </header>

      {/* Hero */}
      <section
        className="min-h-screen relative flex items-center"
        style={{
          backgroundImage: 'url(/relax.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Subtle overlay for text readability */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to right, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.05) 50%, transparent 100%)',
          }}
        />

        {/* Text content */}
        <div className="relative z-10 px-8 md:px-16 lg:px-24 max-w-2xl">
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-8"
            style={{
              color: '#fff',
              textShadow: '0 2px 20px rgba(0,0,0,0.15)',
            }}
          >
            Relax, we'll take care of the rest
          </h1>
          <button
            onClick={() => scrollToSection('pricing')}
            className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-medium transition-opacity hover:opacity-70"
            style={{
              color: '#fff',
              textShadow: '0 1px 2px rgba(0,0,0,0.2)',
              ...MONTSERRAT,
            }}
          >
            Get Started
            <span style={{ fontSize: '14px' }}>→</span>
          </button>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" style={{ backgroundColor: '#FBF9F6' }}>

        {/* CREATE - Browser window with actual itinerary view */}
        <div className="py-24 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2
                className="text-3xl md:text-4xl tracking-wide font-light mb-4"
                style={{ color: '#412F21' }}
              >
                Craft experiences that inspire
              </h2>
              <p
                className="text-sm max-w-lg mx-auto"
                style={{ color: '#6B6B6B' }}
              >
                Turn complex itineraries into elegant, day-by-day journeys
              </p>
            </div>

            {/* Itinerary card */}
            <div
              className="rounded-xl overflow-hidden shadow-2xl mx-auto max-w-4xl"
              style={{ border: '1px solid rgba(65, 47, 33, 0.1)' }}
            >
              <div style={{ backgroundColor: '#FBF9F6' }}>
                {/* Cover image */}
                <div
                  className="h-56 relative"
                  style={{
                    backgroundImage: 'url(/feroe.jpg)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center 40%'
                  }}
                >
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #FBF9F6 0%, transparent 50%)' }} />
                  <div className="absolute bottom-4 left-6">
                    <p className="text-xs tracking-widest uppercase mb-1" style={{ color: '#412F21', opacity: 0.6 }}>5 Days</p>
                    <h3 className="text-2xl font-light" style={{ color: '#412F21' }}>Faroe Islands</h3>
                  </div>
                </div>

                {/* Timeline preview */}
                <div className="p-6">
                  <div className="flex gap-6">
                    {/* Day column */}
                    <div className="flex-1">
                      {/* Day Header */}
                      <div className="mb-3 px-5 py-3 rounded-lg" style={{ backgroundColor: '#412F21' }}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[0.55rem] tracking-[0.2em] uppercase text-white/80 mb-0.5">Day One</p>
                            <p className="text-sm text-white">Thursday, June 12</p>
                          </div>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M18 15l-6-6-6 6"/></svg>
                        </div>
                      </div>
                      {/* Event cards */}
                      <div className="space-y-3">
                        <div className="bg-white border border-[#1A1A1A]/8 rounded-md p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#412F21]/10 flex items-center justify-center flex-shrink-0">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#412F21" strokeWidth="2"><path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/></svg>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-[#1A1A1A]">Atlantic Airways RC 455</p>
                              <p className="text-xs text-[#6B6B6B]">Copenhagen → Vágar · 2h 15m</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white border border-[#1A1A1A]/8 rounded-md p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#412F21]/10 flex items-center justify-center flex-shrink-0">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#412F21" strokeWidth="2"><path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-4h6v4"/></svg>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-[#1A1A1A]">Hotel Føroyar</p>
                              <p className="text-xs text-[#6B6B6B]">Check in 3:00 PM · 4 nights</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Second day column */}
                    <div className="flex-1 hidden md:block">
                      <div className="mb-3 px-5 py-3 rounded-lg" style={{ backgroundColor: '#412F21' }}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[0.55rem] tracking-[0.2em] uppercase text-white/80 mb-0.5">Day Two</p>
                            <p className="text-sm text-white">Friday, June 13</p>
                          </div>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
                        </div>
                      </div>
                      {/* Event cards */}
                      <div className="space-y-3">
                        <div className="bg-white border border-[#1A1A1A]/8 rounded-md p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#412F21]/10 flex items-center justify-center flex-shrink-0">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#412F21" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-[#1A1A1A]">Múlafossur Waterfall</p>
                              <p className="text-xs text-[#6B6B6B]">Guided hike · 9:00 AM</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white border border-[#1A1A1A]/8 rounded-md p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#412F21]/10 flex items-center justify-center flex-shrink-0">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#412F21" strokeWidth="2"><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 002-2V2"/><path d="M7 2v20"/><path d="M21 15V2a5 5 0 00-5 5v6c0 1.1.9 2 2 2h3zm0 0v7"/></svg>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-[#1A1A1A]">KOKS Restaurant</p>
                              <p className="text-xs text-[#6B6B6B]">Reservation · 7:00 PM</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* WHITE LABELING Section */}
        <div
          className="py-24 px-6"
          style={{ backgroundColor: 'rgba(65, 47, 33, 0.02)' }}
        >
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* White labeling mock */}
              <div className="order-2 md:order-1">
                <div
                  className="rounded-xl overflow-hidden shadow-lg"
                  style={{
                    backgroundColor: '#faf9f7',
                    border: '1px solid rgba(45, 80, 75, 0.1)'
                  }}
                >
                  {/* Agency header */}
                  <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(45, 80, 75, 0.08)' }}>
                    <div className="flex items-center gap-2">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2C6.5 2 2 6.5 2 12c0 3.5 1.8 6.5 4.5 8.3C7 17 9 14 12 14s5 3 5.5 6.3C20.2 18.5 22 15.5 22 12c0-5.5-4.5-10-10-10z" fill="#2d504b"/>
                        <path d="M12 6c-2 2-3 5-3 8h6c0-3-1-6-3-8z" fill="#5a8a7a"/>
                      </svg>
                      <span className="text-[10px] tracking-[0.12em] uppercase font-medium" style={{ color: '#2d504b' }}>
                        Verdant Journeys
                      </span>
                    </div>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2d504b" strokeWidth="1.5" opacity="0.4"><path d="M4 6h16M4 12h16M4 18h16"/></svg>
                  </div>

                  {/* Itinerary content */}
                  <div className="p-4">
                    {/* Trip title area */}
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="text-[10px] tracking-wider uppercase mb-1" style={{ color: '#2d504b', opacity: 0.5 }}>7 Days · 4 Travelers</p>
                        <p className="text-lg font-light" style={{ color: '#2d504b' }}>Bali Wellness Retreat</p>
                      </div>
                      <div className="flex -space-x-1.5">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-medium" style={{ backgroundColor: 'rgba(45, 80, 75, 0.15)', color: '#2d504b', border: '2px solid #faf9f7' }}>A</div>
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-medium" style={{ backgroundColor: 'rgba(45, 80, 75, 0.25)', color: '#2d504b', border: '2px solid #faf9f7' }}>M</div>
                      </div>
                    </div>

                    {/* Day sections */}
                    <div className="space-y-2">
                      <div className="rounded-md px-3 py-2 flex items-center justify-between" style={{ backgroundColor: '#2d504b' }}>
                        <span className="text-[10px] font-medium" style={{ color: '#faf9f7' }}>Day 1 · Arrival</span>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#faf9f7" strokeWidth="2"><path d="M18 15l-6-6-6 6"/></svg>
                      </div>

                      {/* Events */}
                      <div className="py-2 space-y-2">
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(45, 80, 75, 0.1)' }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#2d504b" strokeWidth="1.5"><path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/></svg>
                          </div>
                          <div>
                            <p className="text-[10px] font-medium" style={{ color: '#2d504b' }}>Singapore Airlines</p>
                            <p className="text-[8px]" style={{ color: '#2d504b', opacity: 0.5 }}>SIN → DPS · 2h 35m</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(45, 80, 75, 0.1)' }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#2d504b" strokeWidth="1.5"><path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6"/></svg>
                          </div>
                          <div>
                            <p className="text-[10px] font-medium" style={{ color: '#2d504b' }}>COMO Shambhala</p>
                            <p className="text-[8px]" style={{ color: '#2d504b', opacity: 0.5 }}>Check in · 3:00 PM</p>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-md px-3 py-2 flex items-center justify-between" style={{ backgroundColor: '#2d504b' }}>
                        <span className="text-[10px] font-medium" style={{ color: '#faf9f7' }}>Day 2 · Ubud</span>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#faf9f7" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
                      </div>

                      <div className="rounded-md px-3 py-2 flex items-center justify-between" style={{ backgroundColor: '#2d504b' }}>
                        <span className="text-[10px] font-medium" style={{ color: '#faf9f7' }}>Day 3 · Rice Terraces</span>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#faf9f7" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Text content */}
              <div className="order-1 md:order-2">
                <h2
                  className="text-3xl md:text-4xl tracking-wide font-light mb-4"
                  style={{ color: '#412F21' }}
                >
                  Your brand, your experience
                </h2>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: '#6B6B6B' }}
                >
                  Full white labeling for agencies. Your logo, your colors, your domain. Clients see your brand at every touchpoint while you get the power of Kova behind the scenes.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* SHARING Section */}
        <div
          className="py-24 px-6"
          style={{ backgroundColor: '#FBF9F6' }}
        >
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Text content */}
              <div>
                <h2
                  className="text-3xl md:text-4xl tracking-wide font-light mb-4"
                  style={{ color: '#412F21' }}
                >
                  Share it your way
                </h2>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: '#6B6B6B' }}
                >
                  One link that stays forever in sync. Share via WhatsApp, email, or any channel. Need a printout? Export a beautifully formatted PDF in seconds.
                </p>
              </div>

              {/* Sharing mock */}
              <div>
                {/* Share options bar */}
                <div
                  className="rounded-xl px-4 py-3 mb-3 flex items-center justify-between"
                  style={{
                    backgroundColor: '#FBF9F6',
                    border: '1px solid rgba(65, 47, 33, 0.1)'
                  }}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] tracking-wider uppercase" style={{ color: '#412F21', opacity: 0.5 }}>Share</span>
                    <div className="flex items-center gap-2">
                      {/* Link */}
                      <button
                        onClick={() => navigator.clipboard.writeText('kovatravel.com')}
                        className="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer hover:opacity-70 transition-opacity"
                        style={{ backgroundColor: 'rgba(65, 47, 33, 0.08)' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#412F21" strokeWidth="1.5"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                      </button>
                      {/* WhatsApp */}
                      <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(65, 47, 33, 0.08)' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#412F21"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      </div>
                      {/* Email */}
                      <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(65, 47, 33, 0.08)' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#412F21" strokeWidth="1.5"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 6l-10 7L2 6"/></svg>
                      </div>
                    </div>
                  </div>
                  <div className="h-6 w-px" style={{ backgroundColor: 'rgba(65, 47, 33, 0.1)' }} />
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      {/* PDF */}
                      <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(65, 47, 33, 0.08)' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#412F21" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/><path d="M9 15h6"/><path d="M9 11h6"/></svg>
                      </div>
                    </div>
                    <span className="text-[10px] tracking-wider uppercase" style={{ color: '#412F21', opacity: 0.5 }}>PDF</span>
                  </div>
                </div>

                <div
                  className="rounded-xl overflow-hidden shadow-lg"
                  style={{
                    backgroundColor: '#FBF9F6',
                    border: '1px solid rgba(65, 47, 33, 0.1)'
                  }}
                >
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 px-3 py-1 rounded-full" style={{ backgroundColor: 'rgba(139, 115, 85, 0.12)' }}>
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#412F21' }} />
                        <p className="text-[10px] tracking-wider uppercase font-medium" style={{ color: '#8B7355' }}>Live</p>
                      </div>
                      <p className="text-xs" style={{ color: '#6B6B6B' }}>Just now</p>
                    </div>
                    <div
                      className="h-32 rounded-lg mb-3"
                      style={{
                        backgroundImage: 'url(/mexico.jpeg)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center top'
                      }}
                    />
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-lg font-light" style={{ color: '#412F21' }}>Mexico City & Oaxaca</p>
                        <p className="text-xs" style={{ color: '#6B6B6B' }}>March 15 – 22, 2026 · 8 days</p>
                      </div>
                      <div className="flex -space-x-2">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-medium" style={{ backgroundColor: 'rgba(139, 115, 85, 0.25)', color: '#412F21', border: '2px solid #FBF9F6' }}>JD</div>
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-medium" style={{ backgroundColor: 'rgba(139, 115, 85, 0.4)', color: '#412F21', border: '2px solid #FBF9F6' }}>MK</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* VIEW - Mobile experience */}
        <div
          className="py-24 px-6"
          style={{ backgroundColor: 'rgba(65, 47, 33, 0.03)' }}
        >
          <div className="max-w-4xl mx-auto flex flex-col-reverse md:flex-col">
            {/* iPhone mockup with halo */}
            <div className="flex justify-center md:mb-12 mt-12 md:mt-0">
              <div className="relative">
                {/* Halo/backdrop - Kova brown glow behind phone */}
                <div
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-[60px]"
                  style={{
                    width: '340px',
                    height: '620px',
                    backgroundColor: '#8B7355',
                    filter: 'blur(50px)',
                    opacity: 0.35,
                  }}
                />
                {/* iPhone frame */}
                <div
                  className="relative"
                  style={{
                    width: '280px',
                    height: '572px',
                    borderRadius: '44px',
                    backgroundColor: '#1a1a1a',
                    padding: '4px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255,255,255,0.1) inset',
                  }}
                >
                {/* Side button (right) */}
                <div
                  className="absolute"
                  style={{
                    right: '-2px',
                    top: '120px',
                    width: '3px',
                    height: '32px',
                    backgroundColor: '#2a2a2a',
                    borderRadius: '0 2px 2px 0',
                  }}
                />
                {/* Volume buttons (left) */}
                <div
                  className="absolute"
                  style={{
                    left: '-2px',
                    top: '100px',
                    width: '3px',
                    height: '24px',
                    backgroundColor: '#2a2a2a',
                    borderRadius: '2px 0 0 2px',
                  }}
                />
                <div
                  className="absolute"
                  style={{
                    left: '-2px',
                    top: '132px',
                    width: '3px',
                    height: '24px',
                    backgroundColor: '#2a2a2a',
                    borderRadius: '2px 0 0 2px',
                  }}
                />

                {/* Screen */}
                <div
                  className="relative w-full h-full overflow-hidden"
                  style={{
                    borderRadius: '40px',
                    backgroundColor: '#FBF9F6',
                  }}
                >
                  {/* Dynamic Island */}
                  <div
                    className="absolute left-1/2 transform -translate-x-1/2 z-20"
                    style={{
                      top: '12px',
                      width: '90px',
                      height: '26px',
                      backgroundColor: '#1a1a1a',
                      borderRadius: '20px',
                    }}
                  />
                  {/* Cover image header */}
                  <div
                    className="h-48 relative"
                    style={{
                      backgroundImage: 'url(/andorra.jpg)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    {/* Status bar with KOVA */}
                    <div className="flex items-center justify-between px-6 pt-4">
                      <span className="text-[11px] font-semibold text-white" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>9:41</span>
                      <span className="text-[11px] tracking-[0.3em] text-white font-semibold" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>KOVA</span>
                    </div>
                    {/* Back arrow */}
                    <div className="px-5 mt-2">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }}><path d="M15 18l-6-6 6-6"/></svg>
                    </div>
                  </div>

                  {/* Trip info card */}
                  <div className="px-4 -mt-12 relative z-10">
                    <div
                      className="rounded-xl p-4 shadow-sm"
                      style={{ backgroundColor: '#FBF9F6' }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#412F21" strokeWidth="2" opacity="0.5"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                        <p className="text-[9px] tracking-wider uppercase" style={{ color: '#412F21', opacity: 0.5 }}>Dec 20 - Dec 27 2026 (7 nights)</p>
                      </div>
                      <h3 className="text-lg font-light mb-2" style={{ color: '#412F21' }}>Andorra Ski Escape</h3>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#412F21" strokeWidth="2" opacity="0.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                          <span className="text-[9px]" style={{ color: '#412F21', opacity: 0.6 }}>Soldeu, Grandvalira</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#412F21" strokeWidth="2" opacity="0.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                          <span className="text-[9px]" style={{ color: '#412F21', opacity: 0.6 }}>4 Travelers</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Kova Companion */}
                  <div className="px-4 mt-3 flex items-center justify-between">
                    <div
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                      style={{ backgroundColor: 'rgba(65, 47, 33, 0.06)' }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#412F21" strokeWidth="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                      <span className="text-[9px] font-medium" style={{ color: '#412F21' }}>Kova Companion</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(65, 47, 33, 0.06)' }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#412F21" strokeWidth="1.5"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/></svg>
                      </div>
                      <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(65, 47, 33, 0.06)' }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#412F21" strokeWidth="1.5"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
                      </div>
                    </div>
                  </div>

                  {/* Day headers */}
                  <div className="px-4 mt-3 space-y-2">
                    {/* Day 1 expanded */}
                    <div
                      className="rounded-lg px-3 py-2 flex items-center justify-between"
                      style={{ backgroundColor: '#412F21' }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-medium" style={{ color: '#FBF9F6' }}>Day 1</span>
                        <span className="text-[9px]" style={{ color: '#FBF9F6', opacity: 0.6 }}>• Sunday, Dec 20</span>
                      </div>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FBF9F6" strokeWidth="2"><path d="M18 15l-6-6-6 6"/></svg>
                    </div>

                    {/* Activities */}
                    <div className="space-y-2 py-1">
                      <div className="flex items-center gap-3 p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(65, 47, 33, 0.03)' }}>
                        <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(65, 47, 33, 0.1)' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#412F21" strokeWidth="1.5"><path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/></svg>
                        </div>
                        <div>
                          <p className="text-[10px] font-medium" style={{ color: '#412F21' }}>Flight to Barcelona</p>
                          <p className="text-[8px] uppercase tracking-wide" style={{ color: '#412F21', opacity: 0.5 }}>Departure 07:15 • 2h 10m</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(65, 47, 33, 0.03)' }}>
                        <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(65, 47, 33, 0.1)' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#412F21" strokeWidth="1.5"><path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6"/></svg>
                        </div>
                        <div>
                          <p className="text-[10px] font-medium" style={{ color: '#412F21' }}>Sport Hotel Hermitage</p>
                          <p className="text-[8px] uppercase tracking-wide" style={{ color: '#412F21', opacity: 0.5 }}>Check in 16:00 • 7 nights</p>
                        </div>
                      </div>
                    </div>

                    {/* Day 2 & 3 collapsed */}
                    <div
                      className="rounded-lg px-3 py-2 flex items-center justify-between"
                      style={{ backgroundColor: '#412F21' }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-medium" style={{ color: '#FBF9F6' }}>Day 2</span>
                        <span className="text-[9px]" style={{ color: '#FBF9F6', opacity: 0.6 }}>• Monday, Dec 21</span>
                      </div>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FBF9F6" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
                    </div>
                    <div
                      className="rounded-lg px-3 py-2 flex items-center justify-between"
                      style={{ backgroundColor: '#412F21' }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-medium" style={{ color: '#FBF9F6' }}>Day 3</span>
                        <span className="text-[9px]" style={{ color: '#FBF9F6', opacity: 0.6 }}>• Tuesday, Dec 22</span>
                      </div>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FBF9F6" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
                    </div>
                  </div>

                  {/* Home indicator */}
                  <div className="flex justify-center py-4">
                    <div className="w-24 h-1 rounded-full" style={{ backgroundColor: 'rgba(65, 47, 33, 0.15)' }} />
                  </div>
                </div>
              </div>
              </div>
            </div>

            {/* Text underneath */}
            <div className="text-center mb-12 md:mb-0">
              <h2
                className="text-3xl md:text-4xl tracking-wide font-light mb-4"
                style={{ color: '#412F21' }}
              >
                Their journey, always with them
              </h2>
              <p
                className="text-sm leading-relaxed max-w-lg mx-auto"
                style={{ color: '#6B6B6B' }}
              >
                Every booking organized day by day. Your travelers see exactly what's happening, when it's happening.
              </p>
            </div>
          </div>
        </div>

      </section>

      {/* Pricing Section */}
      <section
        id="pricing"
        className="py-24 px-6"
        style={{ backgroundColor: 'rgba(65, 47, 33, 0.02)' }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-3xl md:text-4xl tracking-wide font-light mb-4"
              style={{ color: '#412F21' }}
            >
              Simple, transparent pricing
            </h2>
            <p
              className="text-sm max-w-md mx-auto"
              style={{ color: '#6B6B6B' }}
            >
              Start crafting unforgettable journeys today
            </p>
          </div>

          <div className="max-w-md mx-auto">
            {/* Individual - Hidden for now */}
            {false && <div
              className="relative rounded-xl p-8 flex flex-col"
              style={{
                backgroundColor: '#FBF9F6',
                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
              }}
            >
              <div className="mb-8">
                <p
                  className="text-xs tracking-[0.2em] uppercase font-medium"
                  style={{ color: '#412F21' }}
                >
                  Individual
                </p>
              </div>

              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl font-light" style={{ color: '#412F21' }}>$29</span>
                  <span className="text-sm" style={{ color: '#6B6B6B' }}>/mo</span>
                </div>
                <p className="text-xs mt-2" style={{ color: '#6B6B6B' }}>per seat, billed monthly</p>
              </div>

              <div className="space-y-3 mb-8 flex-1">
                {['Unlimited itineraries', 'Client sharing portal', 'Real-time updates', 'Mobile-optimized views'].map((feature) => (
                  <div key={feature} className="flex items-center gap-3">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgba(65, 47, 33, 0.1)' }}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#412F21" strokeWidth="3">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    </div>
                    <span className="text-sm" style={{ color: '#412F21' }}>{feature}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate('/signup')}
                className="w-full py-3.5 rounded-lg text-xs tracking-[0.2em] uppercase font-medium transition-all hover:opacity-90"
                style={{
                  backgroundColor: '#412F21',
                  color: '#FBF9F6',
                  ...MONTSERRAT,
                }}
              >
                Get Started
              </button>
            </div>}

            {/* Agency */}
            <div
              className="relative rounded-xl p-8 flex flex-col"
              style={{
                backgroundColor: '#FBF9F6',
                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
              }}
            >
              <div className="flex items-center justify-between mb-8">
                <p
                  className="text-xs tracking-[0.2em] uppercase font-medium"
                  style={{ color: '#412F21' }}
                >
                  Agency
                </p>
              </div>

              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl font-light" style={{ color: '#412F21' }}>Custom</span>
                </div>
                <p className="text-xs mt-2" style={{ color: '#6B6B6B' }}>tailored to your team size</p>
              </div>

              <div className="space-y-3 mb-8 flex-1">
                {['Unlimited itineraries', 'Mobile app connectivity', 'Team collaboration', 'White labeling', 'Dedicated onboarding'].map((feature) => (
                  <div key={feature} className="flex items-center gap-3">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgba(65, 47, 33, 0.1)' }}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#412F21" strokeWidth="3">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    </div>
                    <span className="text-sm" style={{ color: '#412F21' }}>{feature}</span>
                  </div>
                ))}
              </div>

              <a
                href="mailto:founders@kovatravel.com"
                className="w-full py-3.5 rounded-lg text-xs tracking-[0.2em] uppercase font-medium transition-all hover:opacity-90 text-center block"
                style={{
                  border: '1px solid rgba(65, 47, 33, 0.2)',
                  color: '#412F21',
                  backgroundColor: 'transparent',
                  ...MONTSERRAT,
                }}
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <section id="faq" />

      {/* Footer */}
      <footer
        className="py-12 px-8"
        style={{
          backgroundColor: '#FBF9F6',
          borderTop: '1px solid rgba(65, 47, 33, 0.08)'
        }}
      >
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Logo */}
          <div className="flex items-center">
            {KOVA.logoUrl ? (
              <img
                src={KOVA.logoUrl}
                alt={KOVA.brandName}
                className="h-8 object-contain opacity-70"
                style={{ maxWidth: '120px' }}
              />
            ) : (
              <span
                className="uppercase font-medium"
                style={{
                  color: '#412F21',
                  fontSize: '12px',
                  letterSpacing: '0.5em',
                  opacity: 0.7
                }}
              >
                {KOVA.brandName}
              </span>
            )}
          </div>

          {/* Links */}
          <div className="flex items-center gap-6">
            <a
              href="mailto:founders@kovatravel.com"
              className="text-xs tracking-wider hover:opacity-50 transition-opacity"
              style={{ color: '#412F21', opacity: 0.5 }}
            >
              Contact Us
            </a>
            <button
              onClick={() => navigate('/terms-of-service')}
              className="text-xs tracking-wider hover:opacity-50 transition-opacity"
              style={{ color: '#412F21', opacity: 0.5 }}
            >
              Terms
            </button>
            <button
              onClick={() => navigate('/privacy')}
              className="text-xs tracking-wider hover:opacity-50 transition-opacity"
              style={{ color: '#412F21', opacity: 0.5 }}
            >
              Privacy
            </button>
          </div>

          {/* Copyright */}
          <p
            className="text-xs"
            style={{ color: '#412F21', opacity: 0.3 }}
          >
            © {new Date().getFullYear()} Kova Travel LLC
          </p>
        </div>
      </footer>
    </div>
  );
}
